/**
 * Equity curve, drawn with the same chart library as the price chart.
 *
 * The unit is R multiples, not currency: that is what the backtester
 * produces, and it keeps results comparable across symbols and position
 * sizes.
 */

import { useEffect, useState } from 'react'
import { AreaSeries, ColorType, createChart } from 'lightweight-charts'

import type { EquityPoint } from '@/api/types'
import { COLORS } from '@/theme/chart'

export function EquityCurve({ points }: { points: EquityPoint[] }) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!container || points.length === 0) return

    const positive = (points.at(-1)?.value ?? 0) >= 0
    const line = positive ? COLORS.up : COLORS.down

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: COLORS.background },
        textColor: COLORS.textDim,
        fontSize: 10,
        attributionLogo: false,
      },
      grid: { vertLines: { color: COLORS.grid }, horzLines: { color: COLORS.grid } },
      rightPriceScale: { borderColor: COLORS.border },
      timeScale: { borderColor: COLORS.border, timeVisible: false },
    })

    const series = chart.addSeries(AreaSeries, {
      lineColor: line,
      topColor: positive ? 'rgba(38, 166, 154, 0.30)' : 'rgba(239, 83, 80, 0.30)',
      bottomColor: 'rgba(0, 0, 0, 0)',
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    })

    // Trades can close in the same second; the chart requires strictly
    // ascending, unique timestamps, so nudge duplicates forward.
    let previous = 0
    const data = points.map((point) => {
      const time = Math.max(point.time, previous + 1)
      previous = time
      return { time: time as never, value: point.value }
    })
    series.setData(data)
    chart.timeScale().fitContent()

    const observer = new ResizeObserver(() =>
      chart.applyOptions({ width: container.clientWidth, height: container.clientHeight }),
    )
    observer.observe(container)

    return () => {
      observer.disconnect()
      chart.remove()
    }
  }, [container, points])

  if (points.length === 0) {
    return <div className="grid h-full place-items-center text-2xs text-ink-dim">Không có lệnh nào</div>
  }
  return <div ref={setContainer} className="h-full w-full" />
}
